import * as React from 'react';
import { useRef, useCallback, useState } from 'react';

import {
  PADDING_X,
  PADDING_Y,
  PADDING_BOTTOM,
  Z_INDEX,
  SPACE_Y_SCALE,
  DISPLAY,
  FLEX,
  SIZE_SCALE,
} from '@/components/design-system/tokens';
import type { User } from '@/hooks/useAuth';

import { useEscapeKey, useFocusTrap } from '../hooks/useKeyboardNav';
import {
  MobileMenuHeader,
  SearchButton,
  JumpSection,
  NavigationLinks,
  ResourcesSection,
  PreferencesSection,
  UserSection,
} from '../mobile-menu/components';
import { useSwipeGesture } from '../mobile-menu/hooks/useSwipeGesture';

interface ProviderSection {
  id: string;
  label: string;
  count?: number;
}

interface MobileMenuProps {
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
  scrollToTop: () => void;
  user: User | null;
  newInquiryCount: number;
  currentLanguage: string;
  changeLanguage: (language: string) => void;
  handleLogout: () => void;
  handleSignup: () => void;
  handleLogin: () => void;
  setShowSearch: (show: boolean) => void;
  t: (
    page: string,
    context: string,
    fallback?: string | null,
    variables?: Record<string, unknown>
  ) => string;
  providerSections: ProviderSection[];
  activeSection: string;
}

export const MobileMenu = React.memo(
  ({
    mobileMenuOpen,
    setMobileMenuOpen,
    scrollToTop,
    user,
    newInquiryCount,
    currentLanguage,
    changeLanguage,
    handleLogout,
    handleSignup,
    handleLogin,
    setShowSearch,
    t,
    providerSections,
    activeSection,
  }: MobileMenuProps) => {
    const menuRef = useRef(null);
    const [languageOpen, setLanguageOpen] = useState(false);
    const [adminOpen, setAdminOpen] = useState(false);
    const [partnerOpen, setPartnerOpen] = useState(false);
    const [legalOpen, setLegalOpen] = useState(false);
    const [resourcesOpen, setResourcesOpen] = useState(false);
    const [jumpOpen, setJumpOpen] = useState(false);

    // Close handler
    const closeMenu = useCallback(() => {
      setMobileMenuOpen(false);
    }, [setMobileMenuOpen]);

    // Scroll to section function for provider detail pages
    const scrollToSection = useCallback(
      (sectionId: string) => {
        window.history.pushState(null, '', `#${sectionId}`);

        const element = document.querySelector(`#${sectionId}`);
        if (element) {
          const offset = 110;
          const elementPosition = element.getBoundingClientRect().top + window.scrollY;
          const offsetPosition = elementPosition - offset;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        }
        closeMenu();
      },
      [closeMenu]
    );

    // Custom hooks
    useEscapeKey(mobileMenuOpen, closeMenu);
    useFocusTrap(mobileMenuOpen, menuRef);
    useSwipeGesture(mobileMenuOpen, closeMenu, menuRef);

    if (!mobileMenuOpen) return null;

    return (
      <div
        ref={menuRef}
        className={`fixed inset-0 bg-gradient-to-b from-white to-gray-50 overflow-y-auto`}
        style={{ zIndex: Z_INDEX.highest }}
        role="dialog"
        aria-label={t('aria', 'mobile_menu', 'Mobile navigation menu')}
        aria-modal="true"
      >
        <div
          className={`${DISPLAY.flex} ${FLEX.flexCol} ${SIZE_SCALE.fullHeight} ${PADDING_BOTTOM.safe}`}
        >
          <MobileMenuHeader onClose={closeMenu} t={t} />

          <nav className={`flex-1 ${PADDING_X['4']} ${PADDING_Y['6']} ${SPACE_Y_SCALE['1']}`}>
            <SearchButton
              onSearch={() => {
                setShowSearch(true);
                closeMenu();
              }}
              t={t}
            />

            <JumpSection
              providerSections={providerSections}
              activeSection={activeSection}
              isOpen={jumpOpen}
              onToggle={() => setJumpOpen(!jumpOpen)}
              onNavigate={scrollToSection}
              t={t}
            />

            <NavigationLinks scrollToTop={scrollToTop} onClose={closeMenu} user={user} t={t} />

            <ResourcesSection
              isOpen={resourcesOpen}
              onToggle={() => setResourcesOpen(!resourcesOpen)}
              scrollToTop={scrollToTop}
              onClose={closeMenu}
              t={t}
            />

            <PreferencesSection
              currentLanguage={currentLanguage}
              languageOpen={languageOpen}
              legalOpen={legalOpen}
              onLanguageToggle={() => setLanguageOpen(!languageOpen)}
              onLegalToggle={() => setLegalOpen(!legalOpen)}
              onLanguageChange={code => {
                changeLanguage(code);
                setLanguageOpen(false);
              }}
              scrollToTop={scrollToTop}
              onClose={closeMenu}
              t={t}
            />
          </nav>

          <UserSection
            user={user}
            newInquiryCount={newInquiryCount}
            adminOpen={adminOpen}
            partnerOpen={partnerOpen}
            onAdminToggle={() => setAdminOpen(!adminOpen)}
            onPartnerToggle={() => setPartnerOpen(!partnerOpen)}
            handleLogin={handleLogin}
            handleSignup={handleSignup}
            handleLogout={handleLogout}
            onClose={closeMenu}
            t={t}
          />
        </div>
      </div>
    );
  }
);

MobileMenu.displayName = 'MobileMenu';
